import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Scissors, Layers, Palette, Type, Volume2, Zap, 
  Play, Pause, RotateCcw, Settings, Sliders,
  Film, Music, Sparkles, ChevronDown, ChevronRight
} from 'lucide-react';

interface EditingToolsProps {
  fileType: 'audio' | 'video';
  onToolAction: (action: string, parameters: any) => void;
  isProcessing: boolean;
}

const EditingTools: React.FC<EditingToolsProps> = ({ 
  fileType, 
  onToolAction, 
  isProcessing 
}) => {
  const [activeSection, setActiveSection] = useState<string>('basic');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic']));

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const videoTools = [
    {
      id: 'basic',
      title: 'Basic Editing',
      icon: Scissors,
      tools: [
        {
          name: 'Cut/Trim',
          action: 'cut',
          icon: Scissors,
          description: 'Cut or trim video clips',
          inputs: [
            { name: 'startTime', label: 'Start Time (s)', type: 'number', defaultValue: 0 },
            { name: 'endTime', label: 'End Time (s)', type: 'number', defaultValue: 10 }
          ]
        },
        {
          name: 'Merge Videos',
          action: 'merge',
          icon: Layers,
          description: 'Combine multiple video clips',
          inputs: []
        },
        {
          name: 'Speed Control',
          action: 'speed',
          icon: Zap,
          description: 'Change playback speed',
          inputs: [
            { name: 'speed', label: 'Speed Factor', type: 'range', min: 0.25, max: 4, step: 0.25, defaultValue: 1 }
          ]
        }
      ]
    },
    {
      id: 'filters',
      title: 'Filters & Effects',
      icon: Palette,
      tools: [
        {
          name: 'Black & White',
          action: 'filter',
          icon: Palette,
          description: 'Convert to grayscale',
          inputs: [
            { name: 'filterType', type: 'hidden', defaultValue: 'grayscale' }
          ]
        },
        {
          name: 'Sepia/Vintage',
          action: 'filter',
          icon: Palette,
          description: 'Apply vintage sepia tone',
          inputs: [
            { name: 'filterType', type: 'hidden', defaultValue: 'sepia' }
          ]
        },
        {
          name: 'Cinematic',
          action: 'filter',
          icon: Palette,
          description: 'Professional cinematic look',
          inputs: [
            { name: 'filterType', type: 'hidden', defaultValue: 'cinematic' }
          ]
        },
        {
          name: 'Brightness',
          action: 'filter',
          icon: Palette,
          description: 'Adjust brightness levels',
          inputs: [
            { name: 'filterType', type: 'hidden', defaultValue: 'brightness' },
            { name: 'intensity', label: 'Brightness', type: 'range', min: 0.5, max: 2, step: 0.1, defaultValue: 1.2 }
          ]
        },
        {
          name: 'Contrast',
          action: 'filter',
          icon: Palette,
          description: 'Enhance contrast',
          inputs: [
            { name: 'filterType', type: 'hidden', defaultValue: 'contrast' },
            { name: 'intensity', label: 'Contrast', type: 'range', min: 0.5, max: 2, step: 0.1, defaultValue: 1.3 }
          ]
        }
      ]
    },
    {
      id: 'text',
      title: 'Text & Overlays',
      icon: Type,
      tools: [
        {
          name: 'Add Text',
          action: 'text',
          icon: Type,
          description: 'Add text overlay',
          inputs: [
            { name: 'text', label: 'Text Content', type: 'text', defaultValue: 'Sample Text' },
            { name: 'position', label: 'Position', type: 'select', options: ['top', 'center', 'bottom'], defaultValue: 'bottom' },
            { name: 'fontSize', label: 'Font Size', type: 'range', min: 12, max: 72, step: 2, defaultValue: 24 }
          ]
        },
        {
          name: 'Captions',
          action: 'captions',
          icon: Type,
          description: 'Add subtitle captions',
          inputs: [
            { name: 'text', label: 'Caption Text', type: 'text', defaultValue: 'Caption' }
          ]
        }
      ]
    }
  ];

  const audioTools = [
    {
      id: 'basic',
      title: 'Basic Editing',
      icon: Scissors,
      tools: [
        {
          name: 'Cut/Trim',
          action: 'cut',
          icon: Scissors,
          description: 'Cut or trim audio clips',
          inputs: [
            { name: 'startTime', label: 'Start Time (s)', type: 'number', defaultValue: 0 },
            { name: 'endTime', label: 'End Time (s)', type: 'number', defaultValue: 10 }
          ]
        },
        {
          name: 'Merge Audio',
          action: 'merge',
          icon: Layers,
          description: 'Combine multiple audio tracks',
          inputs: []
        },
        {
          name: 'Volume Control',
          action: 'volume',
          icon: Volume2,
          description: 'Adjust audio volume',
          inputs: [
            { name: 'gain', label: 'Volume (dB)', type: 'range', min: -20, max: 20, step: 1, defaultValue: 0 }
          ]
        }
      ]
    },
    {
      id: 'effects',
      title: 'Audio Effects',
      icon: Zap,
      tools: [
        {
          name: 'Pitch Control',
          action: 'pitch',
          icon: Zap,
          description: 'Change audio pitch',
          inputs: [
            { name: 'semitones', label: 'Pitch (semitones)', type: 'range', min: -12, max: 12, step: 1, defaultValue: 0 }
          ]
        },
        {
          name: 'Tempo Control',
          action: 'tempo',
          icon: Zap,
          description: 'Change playback speed',
          inputs: [
            { name: 'factor', label: 'Tempo Factor', type: 'range', min: 0.5, max: 2, step: 0.1, defaultValue: 1 }
          ]
        },
        {
          name: 'Noise Reduction',
          action: 'noise_reduction',
          icon: Sparkles,
          description: 'Remove background noise',
          inputs: [
            { name: 'strength', label: 'Strength', type: 'range', min: 0.1, max: 1, step: 0.1, defaultValue: 0.5 }
          ]
        },
        {
          name: 'Reverb',
          action: 'reverb',
          icon: Sparkles,
          description: 'Add reverb effect',
          inputs: [
            { name: 'roomSize', label: 'Room Size', type: 'range', min: 0.1, max: 1, step: 0.1, defaultValue: 0.5 }
          ]
        }
      ]
    },
    {
      id: 'voice',
      title: 'Voice Effects',
      icon: Music,
      tools: [
        {
          name: 'Robotic Voice',
          action: 'voice_conversion',
          icon: Music,
          description: 'Convert to robotic voice',
          inputs: [
            { name: 'type', type: 'hidden', defaultValue: 'robotic' },
            { name: 'intensity', label: 'Intensity', type: 'range', min: 0.1, max: 1, step: 0.1, defaultValue: 0.8 }
          ]
        },
        {
          name: 'Female Voice',
          action: 'voice_conversion',
          icon: Music,
          description: 'Convert to female voice',
          inputs: [
            { name: 'type', type: 'hidden', defaultValue: 'female' }
          ]
        },
        {
          name: 'Male Voice',
          action: 'voice_conversion',
          icon: Music,
          description: 'Convert to male voice',
          inputs: [
            { name: 'type', type: 'hidden', defaultValue: 'male' }
          ]
        }
      ]
    }
  ];

  const tools = fileType === 'video' ? videoTools : audioTools;

  const handleToolSubmit = (tool: any, formData: FormData) => {
    const parameters: any = {};
    
    tool.inputs.forEach((input: any) => {
      const value = formData.get(input.name);
      if (value !== null) {
        if (input.type === 'number' || input.type === 'range') {
          parameters[input.name] = parseFloat(value as string);
        } else {
          parameters[input.name] = value;
        }
      } else if (input.defaultValue !== undefined) {
        parameters[input.name] = input.defaultValue;
      }
    });

    onToolAction(tool.action, parameters);
  };

  return (
    <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-800 bg-black">
        <div className="flex items-center space-x-2">
          {fileType === 'video' ? (
            <Film className="w-5 h-5" />
          ) : (
            <Music className="w-5 h-5" />
          )}
          <h3 className="text-lg font-semibold">
            {fileType === 'video' ? 'Video' : 'Audio'} Editing Tools
          </h3>
        </div>
      </div>

      {/* Tools Sections */}
      <div className="max-h-[70vh] overflow-y-auto">
        {tools.map((section) => (
          <div key={section.id} className="border-b border-gray-800 last:border-b-0">
            {/* Section Header */}
            <motion.button
              whileHover={{ backgroundColor: 'rgba(55, 65, 81, 0.5)' }}
              onClick={() => toggleSection(section.id)}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-800 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <section.icon className="w-5 h-5" />
                <span className="font-medium">{section.title}</span>
              </div>
              {expandedSections.has(section.id) ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </motion.button>

            {/* Section Content */}
            <AnimatePresence>
              {expandedSections.has(section.id) && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 pt-0 space-y-4">
                    {section.tools.map((tool, toolIndex) => (
                      <motion.div
                        key={toolIndex}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: toolIndex * 0.05 }}
                        className="bg-gray-800 rounded-lg p-4 border border-gray-700"
                      >
                        <div className="flex items-center space-x-2 mb-3">
                          <tool.icon className="w-4 h-4" />
                          <h4 className="font-medium">{tool.name}</h4>
                        </div>
                        
                        <p className="text-sm text-gray-400 mb-3">{tool.description}</p>

                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            const formData = new FormData(e.currentTarget);
                            handleToolSubmit(tool, formData);
                          }}
                          className="space-y-3"
                        >
                          {tool.inputs.map((input: any, inputIndex: number) => (
                            <div key={inputIndex}>
                              {input.type === 'hidden' ? (
                                <input
                                  type="hidden"
                                  name={input.name}
                                  defaultValue={input.defaultValue}
                                />
                              ) : (
                                <>
                                  <label className="block text-sm font-medium text-gray-300 mb-1">
                                    {input.label}
                                  </label>
                                  
                                  {input.type === 'range' ? (
                                    <div className="space-y-2">
                                      <input
                                        type="range"
                                        name={input.name}
                                        min={input.min}
                                        max={input.max}
                                        step={input.step}
                                        defaultValue={input.defaultValue}
                                        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                                      />
                                      <div className="flex justify-between text-xs text-gray-500">
                                        <span>{input.min}</span>
                                        <span>{input.max}</span>
                                      </div>
                                    </div>
                                  ) : input.type === 'select' ? (
                                    <select
                                      name={input.name}
                                      defaultValue={input.defaultValue}
                                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-white focus:outline-none"
                                    >
                                      {input.options.map((option: string) => (
                                        <option key={option} value={option}>
                                          {option.charAt(0).toUpperCase() + option.slice(1)}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <input
                                      type={input.type}
                                      name={input.name}
                                      defaultValue={input.defaultValue}
                                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-white focus:outline-none"
                                    />
                                  )}
                                </>
                              )}
                            </div>
                          ))}

                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            type="submit"
                            disabled={isProcessing}
                            className="w-full bg-white text-black px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                          >
                            <tool.icon className="w-4 h-4" />
                            <span>{isProcessing ? 'Processing...' : `Apply ${tool.name}`}</span>
                          </motion.button>
                        </form>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="p-4 bg-black border-t border-gray-800">
        <h4 className="text-sm font-medium text-gray-400 mb-3">Quick Actions</h4>
        <div className="grid grid-cols-2 gap-2">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onToolAction('normalize', {})}
            disabled={isProcessing}
            className="bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded text-sm transition-colors disabled:opacity-50"
          >
            Enhance
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onToolAction('reset', {})}
            disabled={isProcessing}
            className="bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded text-sm transition-colors disabled:opacity-50"
          >
            Reset
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default EditingTools;